import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { useTranslation } from '../../shared/i18n';
import { HorizontalLtrScrollView } from '../../components/ui/HorizontalLtrScrollView';
import { centeredLabelText } from '../../shared/theme/sharedStyles';

interface MenuFlowFoodTypeBarProps {
  foodTypes: string[];
  /** Optional map from food type value to localized display label. */
  foodTypeLabels?: Record<string, string>;
  selectedFoodType: string;
  onSelectFoodType: (type: string) => void;
}

function createMenuFlowFoodTypeBarStyles(c: ThemeColors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      alignSelf: 'stretch',
      minHeight: 0,
      width: '100%',
      justifyContent: 'center',
    },
    scroll: {
      flex: 1,
      minHeight: 0,
      maxHeight: '100%',
    },
    tabsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      flexGrow: 1,
      minHeight: '100%',
    },
    tabWrap: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    tabContent: {
      paddingVertical: 0,
      paddingHorizontal: 10,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 22,
      minWidth: 48,
    },
    tabText: {
      ...centeredLabelText,
      fontSize: 13,
      fontWeight: '500',
      color: c.textSecondary,
    },
    tabTextActive: {
      color: c.primary,
      fontWeight: '600',
    },
    underline: {
      position: 'absolute',
      bottom: 2,
      left: 12,
      right: 12,
      height: 2,
      borderRadius: 10,
      backgroundColor: c.primary,
    },
  });
}

const MenuFlowFoodTypeTab = memo(
  ({
    type,
    displayLabel,
    isActive,
    onSelectFoodType,
    styles,
  }: {
    type: string;
    displayLabel: string;
    isActive: boolean;
    onSelectFoodType: (type: string) => void;
    styles: ReturnType<typeof createMenuFlowFoodTypeBarStyles>;
  }) => (
    <Pressable
      onPress={() => onSelectFoodType(type)}
      style={styles.tabWrap}
      hitSlop={{ top: 10, bottom: 10, left: 6, right: 6 }}
      accessibilityLabel={displayLabel}
      accessibilityRole="button"
    >
      <View style={styles.tabContent}>
        <Text style={[styles.tabText, isActive && styles.tabTextActive]} numberOfLines={1}>
          {displayLabel}
        </Text>
        {isActive && <View style={styles.underline} />}
      </View>
    </Pressable>
  ),
);

export const MenuFlowFoodTypeBar: React.FC<MenuFlowFoodTypeBarProps> = ({ foodTypes, foodTypeLabels, selectedFoodType, onSelectFoodType }) => {
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createMenuFlowFoodTypeBarStyles(colors), [colors]);

  const labelFor = (type: string) => (type === 'All' ? t('menu.all') : (foodTypeLabels?.[type] ?? type));

  return (
    <View style={styles.container}>
      <HorizontalLtrScrollView
        style={styles.scroll}
        contentContainerStyle={styles.tabsRow}
        horizontal
        showsHorizontalScrollIndicator={false}
      >
        {foodTypes.map(type => (
          <MenuFlowFoodTypeTab
            key={type}
            type={type}
            displayLabel={labelFor(type)}
            isActive={selectedFoodType === type}
            onSelectFoodType={onSelectFoodType}
            styles={styles}
          />
        ))}
      </HorizontalLtrScrollView>
    </View>
  );
};
